// PIQ Advocate Engine - Opus 4.5 Build
// Discovery Page - Social mention feed with advocate marking

import React, { useState } from 'react'
import { useLocation } from 'wouter'
import { 
  Search, 
  RefreshCw, 
  Filter, 
  ExternalLink, 
  UserPlus,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  TrendingUp,
  XCircle,
  Ban,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api-client'

// ============================================================================
// TYPES
// ============================================================================

interface Mention {
  mention_id: string
  source_platform: string
  source_post_id: string
  author_handle: string
  author_display_name: string | null
  author_profile_url: string | null
  author_avatar_url: string | null
  author_follower_count: number | null
  author_verified: boolean
  raw_text: string
  sentiment: string | null
  engagement_metrics: {
    likes?: number
    comments?: number
    shares?: number
    views?: number
    score?: number
    retweets?: number
  }
  url: string | null
  published_at: string | null
  ingested_at: string
  advocate_id: string | null
  status: string
}

interface MentionsResponse {
  mentions: Mention[]
  pagination: {
    page: number
    limit: number
    total: number
  }
}

interface IngestResponse {
  success: boolean
  platform: string
  stats: {
    fetched: number
    ingested: number
    duplicates: number
    errors: number
  }
}

// ============================================================================
// PLATFORM CONFIG
// ============================================================================

const PLATFORMS = [
  { id: 'all', label: 'All Platforms', icon: '🌐' },
  { id: 'tiktok', label: 'TikTok', icon: '🎵' },
  { id: 'instagram', label: 'Instagram', icon: '📸' },
  { id: 'twitter', label: 'Twitter/X', icon: '𝕏' },
  { id: 'reddit', label: 'Reddit', icon: '🔴' },
  { id: 'youtube', label: 'YouTube', icon: '▶️' },
  { id: 'facebook', label: 'Facebook', icon: '📘' },
]

const PLATFORM_COLORS: Record<string, string> = {
  tiktok: 'bg-black text-white',
  instagram: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
  twitter: 'bg-black text-white',
  reddit: 'bg-orange-500 text-white',
  youtube: 'bg-red-600 text-white',
  facebook: 'bg-blue-600 text-white',
}

// ============================================================================
// HOOKS
// ============================================================================

function useMentions(platform: string, page: number = 1, limit: number = 20) {
  return useQuery({
    queryKey: ['advocate-opus', 'mentions', platform, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      })
      if (platform !== 'all') {
        params.set('platform', platform)
      }
      const response = await apiClient.get<{ success: boolean; data: MentionsResponse }>(
        `/advocate-opus/mentions?${params.toString()}`
      )
      // Unwrap nested data structure
      return response.data
    },
  })
}

function useIngestSocial() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (platform: string) => {
      const response = await apiClient.get<IngestResponse>(
        `/advocate-opus/ingest-social?platform=${platform}&limit=50`
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advocate-opus', 'mentions'] })
    },
  })
}

function useCreateAdvocate() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (mention: Mention): Promise<{ success: boolean; advocate_id: string; existing?: boolean }> => {
      // Use raw fetch to properly handle 409 responses
      const response = await fetch('/api/advocate-opus/advocates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          primary_handle: mention.author_handle,
          primary_platform: mention.source_platform,
          display_name: mention.author_display_name || mention.author_handle,
          profile_url: mention.author_profile_url,
          avatar_url: mention.author_avatar_url,
          follower_count: mention.author_follower_count,
          status: 'pending',
        }),
      })
      
      const data = await response.json()
      
      // Handle 409 Conflict - advocate already exists, return the existing ID
      if (response.status === 409 && data.advocate_id) {
        return { success: true, advocate_id: data.advocate_id, existing: true }
      }
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create advocate')
      }
      
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advocate-opus', 'mentions'] })
      queryClient.invalidateQueries({ queryKey: ['advocate-opus', 'advocates'] })
    },
  })
}

function useDismissMention() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ mentionId, status }: { mentionId: string; status: string }) => {
      const response = await fetch(`/api/advocate-opus/mentions/${mentionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      })
      
      if (!response.ok) {
        throw new Error('Failed to update mention')
      }
      
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advocate-opus', 'mentions'] })
    },
  })
}

// ============================================================================
// COMPONENTS
// ============================================================================

function PlatformBadge({ platform }: { platform: string }) {
  const colorClass = PLATFORM_COLORS[platform] || 'bg-gray-500 text-white'
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colorClass}`}>
      {platform}
    </span>
  )
}

function EngagementStats({ metrics }: { metrics: Mention['engagement_metrics'] | null }) {
  if (!metrics) return null
  return (
    <div className="flex items-center gap-4 text-sm text-gray-500">
      {metrics.likes !== undefined && (
        <span className="flex items-center gap-1">
          <Heart className="w-4 h-4" />
          {metrics.likes.toLocaleString()}
        </span>
      )}
      {metrics.comments !== undefined && (
        <span className="flex items-center gap-1">
          <MessageCircle className="w-4 h-4" />
          {metrics.comments.toLocaleString()}
        </span>
      )}
      {metrics.shares !== undefined && (
        <span className="flex items-center gap-1">
          <Share2 className="w-4 h-4" />
          {metrics.shares.toLocaleString()}
        </span>
      )}
      {metrics.views !== undefined && (
        <span className="flex items-center gap-1">
          <Eye className="w-4 h-4" />
          {metrics.views.toLocaleString()}
        </span>
      )}
      {metrics.score !== undefined && (
        <span className="flex items-center gap-1">
          <TrendingUp className="w-4 h-4" />
          {metrics.score.toLocaleString()}
        </span>
      )}
    </div>
  )
}

function MentionCard({ 
  mention, 
  onMarkAsAdvocate,
  onDismiss,
}: { 
  mention: Mention
  onMarkAsAdvocate: (mention: Mention) => void
  onDismiss: (mentionId: string) => void 
}) {
  const timeAgo = mention.published_at 
    ? formatTimeAgo(new Date(mention.published_at))
    : 'Unknown time'

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {mention.author_avatar_url ? (
            <img 
              src={mention.author_avatar_url} 
              alt={mention.author_handle}
              className="w-10 h-10 rounded-full"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 text-sm">
                {mention.author_handle[0]?.toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">
                @{mention.author_handle}
              </span>
              {mention.author_verified && (
                <CheckCircle className="w-4 h-4 text-blue-500" />
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <PlatformBadge platform={mention.source_platform} />
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {timeAgo}
              </span>
              {mention.author_follower_count && (
                <span>{mention.author_follower_count.toLocaleString()} followers</span>
              )}
            </div>
          </div>
        </div>
        
        {mention.sentiment && (
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            mention.sentiment === 'positive' ? 'bg-green-100 text-green-700' :
            mention.sentiment === 'negative' ? 'bg-red-100 text-red-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {mention.sentiment}
          </span>
        )}
      </div>

      {/* Content */}
      <p className="text-gray-700 mb-3 whitespace-pre-wrap">
        {mention.raw_text.length > 300 
          ? mention.raw_text.slice(0, 300) + '...' 
          : mention.raw_text}
      </p>

      {/* Engagement */}
      <EngagementStats metrics={mention.engagement_metrics} />

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100">
        {mention.url && (
          <a
            href={mention.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
          >
            <ExternalLink className="w-4 h-4" />
            View Post
          </a>
        )}
        
        {!mention.advocate_id ? (
          <button
            onClick={() => onMarkAsAdvocate(mention)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded font-medium"
          >
            <UserPlus className="w-4 h-4" />
            Mark as Advocate
          </button>
        ) : (
          <span className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" />
            Advocate Created
          </span>
        )}
        
        {/* Not Relevant button */}
        <button
          onClick={() => onDismiss(mention.mention_id)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded ml-auto"
          title="Mark as not relevant"
        >
          <Ban className="w-4 h-4" />
          Not Relevant
        </button>
      </div>
    </div>
  )
}

function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// ============================================================================
// PROGRESS TRACKING TYPES
// ============================================================================

interface PlatformStatus {
  status: 'pending' | 'fetching' | 'success' | 'error'
  fetched?: number
  ingested?: number
  errorMessage?: string
}

// ============================================================================
// PROGRESS PANEL COMPONENT
// ============================================================================

function FetchProgressPanel({ 
  platformStatuses, 
  currentPlatform,
  onClose,
}: { 
  platformStatuses: Record<string, PlatformStatus>
  currentPlatform: string | null
  onClose: () => void
}) {
  const platforms = PLATFORMS.filter(p => p.id !== 'all')
  const completedCount = Object.values(platformStatuses).filter(
    s => s.status === 'success' || s.status === 'error'
  ).length
  const totalCount = platforms.length
  const progressPercent = (completedCount / totalCount) * 100

  const totalFetched = Object.values(platformStatuses).reduce(
    (sum, s) => sum + (s.fetched || 0), 0
  )
  const totalIngested = Object.values(platformStatuses).reduce(
    (sum, s) => sum + (s.ingested || 0), 0
  )

  const isComplete = completedCount === totalCount

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">
              {isComplete ? 'Fetch Complete!' : 'Fetching Social Mentions...'}
            </h3>
            {isComplete && (
              <button 
                onClick={onClose}
                className="text-white/80 hover:text-white"
              >
                <XCircle className="w-5 h-5" />
              </button>
            )}
          </div>
          {!isComplete && (
            <p className="text-purple-200 text-sm mt-1">
              Scanning platforms for brand mentions
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{completedCount} of {totalCount} platforms</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Platform List */}
        <div className="px-6 py-4 space-y-2 max-h-64 overflow-y-auto">
          {platforms.map(platform => {
            const status = platformStatuses[platform.id] || { status: 'pending' }
            return (
              <div 
                key={platform.id}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  status.status === 'fetching' ? 'bg-purple-50 border border-purple-200' :
                  status.status === 'success' ? 'bg-green-50' :
                  status.status === 'error' ? 'bg-red-50' :
                  'bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">{platform.icon}</span>
                  <div>
                    <span className="font-medium text-gray-900">{platform.label}</span>
                    {status.status === 'fetching' && (
                      <p className="text-xs text-purple-600">Searching for mentions...</p>
                    )}
                    {status.status === 'success' && (
                      <p className="text-xs text-green-600">
                        Found {status.fetched} mentions, {status.ingested} new
                      </p>
                    )}
                    {status.status === 'error' && (
                      <p className="text-xs text-red-600">
                        {status.errorMessage || 'Failed to fetch'}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  {status.status === 'pending' && (
                    <Clock className="w-5 h-5 text-gray-400" />
                  )}
                  {status.status === 'fetching' && (
                    <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                  )}
                  {status.status === 'success' && (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  )}
                  {status.status === 'error' && (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Summary Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Total Mentions Found:</span>
            <span className="font-semibold text-gray-900">{totalFetched}</span>
          </div>
          <div className="flex justify-between text-sm mt-1">
            <span className="text-gray-600">New Mentions Added:</span>
            <span className="font-semibold text-green-600">{totalIngested}</span>
          </div>
          {isComplete && (
            <button
              onClick={onClose}
              className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
            >
              View Results
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function DiscoveryNew() {
  const [, navigate] = useLocation()
  const [selectedPlatform, setSelectedPlatform] = useState('all')
  const [page, setPage] = useState(1)
  const [ingestingPlatform, setIngestingPlatform] = useState<string | null>(null)
  const [showProgressPanel, setShowProgressPanel] = useState(false)
  const [platformStatuses, setPlatformStatuses] = useState<Record<string, PlatformStatus>>({})

  const { data, isLoading, error, refetch } = useMentions(selectedPlatform, page)
  const ingestMutation = useIngestSocial()
  const createAdvocateMutation = useCreateAdvocate()
  const dismissMutation = useDismissMention()

  const handleRefresh = async (platform: string) => {
    setIngestingPlatform(platform)
    try {
      await ingestMutation.mutateAsync(platform)
    } finally {
      setIngestingPlatform(null)
    }
  }

  const handleRefreshAll = async () => {
    setIngestingPlatform('all')
    setShowProgressPanel(true)
    
    const platformsToFetch = PLATFORMS.filter(p => p.id !== 'all').map(p => p.id)
    
    // Initialize all platforms as pending
    const initialStatuses: Record<string, PlatformStatus> = {}
    platformsToFetch.forEach(p => {
      initialStatuses[p] = { status: 'pending' }
    })
    setPlatformStatuses(initialStatuses)
    
    for (const platform of platformsToFetch) {
      // Mark as fetching
      setPlatformStatuses(prev => ({
        ...prev,
        [platform]: { status: 'fetching' }
      }))
      
      try {
        const result = await ingestMutation.mutateAsync(platform)
        // Mark as success
        setPlatformStatuses(prev => ({
          ...prev,
          [platform]: {
            status: 'success',
            fetched: result?.stats?.fetched || 0,
            ingested: result?.stats?.ingested || 0,
          }
        }))
      } catch (e: any) {
        // Mark as error
        setPlatformStatuses(prev => ({
          ...prev,
          [platform]: {
            status: 'error',
            errorMessage: e?.message || 'Failed to fetch',
          }
        }))
      }
    }
    
    setIngestingPlatform(null)
  }

  const handleCloseProgress = () => {
    setShowProgressPanel(false)
    setPlatformStatuses({})
    refetch() // Refresh the mentions list
  }

  const handleMarkAsAdvocate = async (mention: Mention) => {
    try {
      const result = await createAdvocateMutation.mutateAsync(mention)
      if (result.advocate_id) {
        // Navigate to advocate profile (whether new or existing)
        navigate(`/advocate-opus/advocates/${result.advocate_id}`)
      }
    } catch (e) {
      console.error('Failed to create advocate:', e)
      alert('Failed to create advocate. Please try again.')
    }
  }

  const handleDismiss = async (mentionId: string) => {
    try {
      await dismissMutation.mutateAsync({ mentionId, status: 'dismissed' })
    } catch (e) {
      console.error('Failed to dismiss mention:', e)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Panel Modal */}
      {showProgressPanel && (
        <FetchProgressPanel
          platformStatuses={platformStatuses}
          currentPlatform={ingestingPlatform}
          onClose={handleCloseProgress}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Advocate Discovery
                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                  Opus Build
                </span>
              </h1>
              <p className="text-gray-500 mt-1">
                Find and recruit brand advocates from social mentions
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* View Advocates Button */}
              <button
                onClick={() => navigate('/advocate-opus/advocates')}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <UserPlus className="w-4 h-4" />
                View Advocates
              </button>

            {/* Refresh Dropdown */}
            <div className="relative group">
              <button className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                <RefreshCw className={`w-4 h-4 ${ingestingPlatform ? 'animate-spin' : ''}`} />
                Fetch New Mentions
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                {/* Fetch All option */}
                <button
                  onClick={() => handleRefreshAll()}
                  disabled={ingestingPlatform !== null}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2 disabled:opacity-50 border-b border-gray-200 font-medium text-purple-600"
                >
                  <span>🚀</span>
                  Fetch All Platforms
                  {ingestingPlatform === 'all' && (
                    <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                  )}
                </button>
                {/* Individual platforms */}
                {PLATFORMS.filter(p => p.id !== 'all').map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => handleRefresh(platform.id)}
                    disabled={ingestingPlatform !== null}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                  >
                    <span>{platform.icon}</span>
                    {platform.label}
                    {ingestingPlatform === platform.id && (
                      <Loader2 className="w-4 h-4 animate-spin ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            </div>
          </div>

          {/* Platform Filters */}
          <div className="flex items-center gap-2 mt-4">
            {PLATFORMS.map(platform => (
              <button
                key={platform.id}
                onClick={() => {
                  setSelectedPlatform(platform.id)
                  setPage(1)
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedPlatform === platform.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {platform.icon} {platform.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Status Banner */}
        {ingestMutation.isSuccess && ingestMutation.data && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <span className="font-medium text-green-800">
                Fetched {ingestMutation.data.stats.fetched} mentions from {ingestMutation.data.platform}
              </span>
              <span className="text-green-600 ml-2">
                ({ingestMutation.data.stats.ingested} new, {ingestMutation.data.stats.duplicates} duplicates)
              </span>
            </div>
          </div>
        )}

        {ingestMutation.isError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-800">
              Failed to fetch mentions. Please try again.
            </span>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-gray-500">Failed to load mentions</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Mentions Grid */}
        {data && (
          <>
            {data.mentions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No mentions found
                </h3>
                <p className="text-gray-500 mb-4">
                  Click "Fetch New Mentions" to pull social data
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.mentions.map(mention => (
                  <MentionCard
                    key={mention.mention_id}
                    mention={mention}
                    onMarkAsAdvocate={handleMarkAsAdvocate}
                    onDismiss={handleDismiss}
                  />
                ))}
              </div>
            )}

            {/* Pagination */}
            {data.pagination.total > data.pagination.limit && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-gray-500">
                  Page {page} of {Math.ceil(data.pagination.total / data.pagination.limit)}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= Math.ceil(data.pagination.total / data.pagination.limit)}
                  className="px-4 py-2 bg-white border border-gray-200 rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
